/* eslint-disable react/forbid-prop-types */
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Downshift from 'downshift';
import { makeStyles } from 'tss-react/mui';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ClearIcon from '@mui/icons-material/Clear';

import { getIn } from 'formik';
import HintText from 'components/labels/HintText';
import { List, ListItem, ListItemText } from '@mui/material';

const useStyles = makeStyles()((theme) => ({
  menuContainer: {
    position: 'absolute',
    zIndex: 200,
    width: '100%',
    top: '55px',
    left: '8px',
  },
  noResultsContainer: {
    padding: theme.spacing(1),
  },
  inputField: {
    margin: theme.spacing(1),
  },
  root: {
    position: 'relative',
  },
}));

function debounce(func, wait) {
  let timeout;
  return function debounced(...args) {
    const context = this;
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      timeout = null;
      func.apply(context, args);
    }, wait);
  };
}

// COMPONENT
function FormikDownshift({
  disabled,
  label,
  placeholder,
  field,
  form: { touched, errors, setFieldValue },
  getSuggestionLabel,
  getListSuggestionLabel,
  onChangeSearchTerm,
  onChange,
  onChangeOverrider,
  areItemsDifferentChecker,
  noOptionsNode,
  menuContainerStyle,
}) {
  const { classes } = useStyles();
  const [suggestions, setSuggestions] = useState();
  const [isLoading, setIsLoading] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceOnChange = useCallback(
    debounce((query) => {
      onChangeSearchTerm(query)
        .then((resp) => {
          setSuggestions(resp);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, 450),
    [onChangeSearchTerm],
  );

  const touch = getIn(touched, field.name);
  const error = getIn(errors, field.name);

  const suggestionIsEmptyOrZero = suggestions == null || suggestions.length === 0;

  return (
    <Downshift
      selectedItemChanged={areItemsDifferentChecker}
      onChange={(selectedItem) => {
        if (onChangeOverrider) {
          onChangeOverrider(selectedItem, field.name);
        } else {
          setFieldValue(field.name, selectedItem);
          if (onChange) {
            onChange(field.name, selectedItem);
          }
        }

        // if clearing, remove suggestions
        if (selectedItem == null) {
          setSuggestions([]);
        }
      }}
      selectedItem={field.value?.id != null ? field.value : null}
      itemToString={(item) => (item ? getSuggestionLabel(item) : '')}
    >
      {({
        getInputProps,
        getItemProps,
        getLabelProps,
        getMenuProps,
        isOpen,
        selectedItem,
        clearSelection,
        openMenu,
        closeMenu,
        inputValue: currentInputValue,
        highlightedIndex,
        getRootProps,
      }) => (
        <div {...getRootProps({}, { suppressRefError: true })} className={classes.root}>
          <TextField
            variant="standard"
            disabled={disabled}
            label={label}
            placeholder={placeholder}
            InputLabelProps={{
              ...getLabelProps({ shrink: true }),
            }}
            InputProps={{
              ...getInputProps({
                onKeyDown: (event) => {
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    event.stopPropagation();
                  }
                },
                onFocus: () => {
                  openMenu();
                },
                autoComplete: 'off',
                endAdornment:
                  selectedItem != null ? (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Remove Item"
                        onClick={clearSelection}
                        size="small"
                        disabled={disabled}
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="Expand/Collapse Dropdown"
                        onClick={() => {
                          if (isOpen) {
                            closeMenu();
                          } else {
                            openMenu();
                          }
                        }}
                      >
                        {isOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                onChange: (event) => {
                  // eslint-disable-next-line prefer-destructuring
                  const currentInputValue1 = event.target.value;

                  if (currentInputValue1 == null || currentInputValue1 === '') {
                    setIsLoading(false);
                    setSuggestions(null);
                  } else {
                    setIsLoading(true);
                    debounceOnChange(currentInputValue1);
                  }
                },
              }),
            }}
            error={touch && error != null}
            helperText={touch && error != null ? (error?.id != null ? error.id : error) : null}
            className={classes.inputField}
            fullWidth
          />

          {isOpen ? (
            <Paper elevation={2} className={classes.menuContainer} style={menuContainerStyle}>
              <List {...getMenuProps()} dense>
                {suggestionIsEmptyOrZero && noOptionsNode && (
                  <div className={classes.noResultsContainer}>
                    {noOptionsNode(
                      currentInputValue,
                      selectedItem,
                      closeMenu,
                      isLoading,
                      suggestions == null,
                    )}
                  </div>
                )}
                {suggestionIsEmptyOrZero && !noOptionsNode && (
                  <HintText className={classes.noResultsContainer}>
                    Nenhum resultado. {currentInputValue.trim().length === 0 && ' Digita para buscar...'}
                  </HintText>
                )}
                {suggestions?.length > 0 &&
                  suggestions.map((suggestion, index) => {
                    const isSelected = selectedItem != null && selectedItem.id === suggestion.id;
                    return (
                      <ListItem
                        {...getItemProps({
                          key: suggestion.id,
                          index,
                          item: suggestion,
                          style: {
                            backgroundColor: highlightedIndex === index ? 'lightgray' : 'white',
                            fontWeight: isSelected ? 'bold' : 'normal',
                          },
                        })}
                        selected={isSelected}
                        // component="div"
                        button
                      >
                        <ListItemText
                          primary={
                            getListSuggestionLabel
                              ? getListSuggestionLabel(suggestion)
                              : getSuggestionLabel(suggestion)
                          }
                        />
                      </ListItem>
                    );
                  })}
              </List>
            </Paper>
          ) : null}
        </div>
      )}
    </Downshift>
  );
}

FormikDownshift.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  field: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  onChangeSearchTerm: PropTypes.func.isRequired,
  getSuggestionLabel: PropTypes.func.isRequired,
  getListSuggestionLabel: PropTypes.func,
  onChange: PropTypes.func,
  areItemsDifferentChecker: PropTypes.func,
};

FormikDownshift.defaultProps = {
  label: null,
  onChange: null,
  placeholder: 'Selecione...',
  areItemsDifferentChecker: (prevItem, item) => {
    return (
      (prevItem == null && item != null) ||
      (prevItem != null && item == null) ||
      (prevItem != null && item != null && prevItem.id !== item.id)
    );
  },
};

export default FormikDownshift;
